<template>
	<div class="cat"
	:class="[stateClass, {
		'can-feed': canFeed,
		'sm-scale': scale < 0.4,
	}]"
	@mousedown.left="tryFeed">
		<CatAnimator
			:breed="breed"
			:state="stateClass"
			:initialState="initialState"
			:walkDistance="walkDistance"
			@feedingComplete="completeRemoval"
			@walkComplete="completeWalk"
			@hasDied="$emit('died')"
		/>
	</div>
</template>

<script>
import soundFx from '@/mixins/sound-fx';
import CatAnimator from '@/components/cat/CatAnimator';
import { mapState, mapMutations } from 'vuex';
import { whichAnimationEnd } from '@/mixins/event-detection';

const animationEnd = whichAnimationEnd();

export default {
	name: 'Cat',
	components: {
		CatAnimator,
	},
	mixins: [soundFx],
	inject: ["scale", "catComponentType"],
	props: ["breed", "walkDistance", "initialState"],
	data() {
		return {
			timesFed: 0,
			isDead: false,
			isWalking: false,
		};
	},
	computed: {
		...mapState({
			activeCycle: state => state.cycle.active,
			completedGameCycles: state => state.cycle.completedGameCycles,
		}),
		stateClass() {
			const states = {
				satisfied: this.isSatisfied,
				walking: this.isWalking,
				starvation: this.isStarving,
				unsatisfied: this.isUnsatisfied,
				fed: this.isFed,
				death: this.isDead,
			};
			return Object.keys(states).find(state => states[state]);
		},
		isSatisfied() {
			return this.timesFed === this.breed.food_required;
		},
		isFed() {
			return this.timesFed > 0 && !this.isSatisfied && !this.isDead;
		},
		isStarving() {
			return !this.isFed && !this.isSatisfied && !this.isDead;
		},
		isUnsatisfied() {
			return this.isFed && !this.activeCycle;
		},
		canFeed() {
			return this.activeCycle
				&& this.$store.state.food.remainingFood >= 1
				&& !this.isDead
				&& !this.isSatisfied;
		},
	},
	watch: {
		completedGameCycles() {
			this.$nextTick(() => {
				if (!this.isFed && !this.isSatisfied && !this.isDead) {
					this.die();
				}
			});
		},
		isFed(isFed, wasFed) {
			if (isFed && !wasFed) {
				this.$store.commit("decrementUnfedCatCount");
			}
		},
		isSatisfied(isSatisfied) {
			if (isSatisfied) {
				this.incrementCycleAction({ type: "satisfied" });
			}
		},
		isUnsatisfied(isUnsatisfied) {
			if (isUnsatisfied) {
				this.incrementCycleAction({ type: "unsatisfied" });
				this.incrementCycleBalance({ amount: this.breed.food_required - this.timesFed });
			}
		},
	},
	methods: {
		...mapMutations([
			"incrementCycleAction",
			"incrementCycleBalance",
			"activateFoodMessage",
			"incrementDeadCats",
		]),
		tryFeed() {
			if (this.canFeed) {
				this.feed();
			} else if (this.activeCycle) {
				this.shake();
				this.activateFoodMessage();
			}
		},
		feed() {
			this.timesFed += 1;
			this.$store.commit("decrementFood", { amount: 1 });
			this.$emit("fed");

			this.playRandomSound("feed");
		},
		shake() {
			this.$el.classList.add("shake");
			this.$el.addEventListener(animationEnd, () => {
				this.$el.classList.remove("shake");
			});

			this.playRandomSound("empty_bag");
		},
		die() {
			this.isDead = true;
			// this.$emit("died");
			this.incrementDeadCats();
			this.incrementCycleAction({ type: "killed" });
			this.incrementCycleBalance({ amount: this.breed.food_required });
		},
		walk() {
			this.isWalking = true;
		},
		completeWalk() {
			this.isWalking = false;
			this.$emit("walkComplete");
			this.playRandomSound("starvation");
		},
		completeRemoval() {
			this.$emit("remove");
		},
		overrideState(state) {
			switch (state) {
			case "angel":
				this.isDead = true;
				break;
			case "death":
				this.isDead = true;
				break;
			case "walking":
				this.isWalking = true;
				break;
			default:
				break;
			}
		},
	},
	mounted() {
		if (this.isStarving && this.initialState !== "walking") {
			this.playRandomSound("starvation");
		}

		if (this.initialState) {
			this.overrideState(this.initialState);
		}
	},
};
</script>

<style lang="scss" scoped>
	.cat {
		position: relative;
		cursor: pointer;
		touch-action: manipulation;
		background-repeat: no-repeat;
		width: 100%;
		height: 100%;

		&.can-feed {
			transition: transform $ui-transition-duration-short;

			&:active {
				transform: translate(1px, 1px) scale(.95);
				@include mobile { transform: translate(2px, 2px) scale(.9); }

				&.sm-scale {
					transform: translate(3px, 3px) scale(.85);
					@include mobile { transform: translate(4px, 4px) scale(.8); }
				}
			}
		}

		&.fed,
		&.starvation {
			touch-action: manipulation;
			pointer-events: auto;
		}

		&.satisfied,
		&.death {
			cursor: default;
			touch-action: none;
			pointer-events: none;
		}

		&.shake {
			animation: shake 1s;
		}

		.no-click & {
			pointer-events: none;
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/cat/Cat.vue